import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Loader2, Building2, Wallet, Home, AlertTriangle, Wrench, ShieldCheck, FilePlus2, CalendarClock } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { KpiCharts } from "@/components/portal/KpiCharts";
import { ScoreHistory } from "@/components/portal/ScoreHistory";
import { PassRateTrends } from "@/components/portal/PassRateTrends";
import { TrainingImport } from "@/components/portal/TrainingImport";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`);

const KpiCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="technical-card p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <Icon size={16} className={accent ? "text-sky-600" : "text-green-700"} />
    </div>
    <p className={`font-mono text-4xl font-semibold tracking-tight ${accent ? "text-sky-600" : "text-slate-900"}`}>{value}</p>
    {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function Portal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [training, setTraining] = useState(null);
  const [batches, setBatches] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/portal/status");
      setData(res.data);
      api.get("/portal/history").then((h) => setHistory(h.data.history || [])).catch(() => {});
      api.get("/training/results/latest").then((t) => setTraining(t.data.batch)).catch(() => {});
      api.get("/training/batches").then((b) => setBatches(b.data.batches || [])).catch(() => {});
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Could not load portal status");
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => { setUser(res.data); load(); })
      .catch(() => navigate("/login"));
  }, [navigate, load]);

  const sync = async () => {
    setSyncing(true);
    toast.info("Syncing with ScheduleMyRent…", { description: "Live pull takes about 30 seconds." });
    try {
      const res = await api.post("/portal/sync", {}, { timeout: 130000 });
      setData(res.data);
      if (res.data.connected) {
        toast.success("Portfolio synced", { description: "KPIs updated from your landlord dashboard." });
        api.get("/portal/history").then((h) => setHistory(h.data.history || [])).catch(() => {});
      } else {
        toast.error("Sync failed", { description: res.data.error || "Unknown error" });
      }
    } catch (err) {
      toast.error("Sync failed", { description: typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Connection error" });
    } finally {
      setSyncing(false);
    }
  };

  const k = data?.kpis || {};
  const collections = k.collections || [];

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB]" data-testid="portal-page">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-[#EDF4FB]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="OPERA360 home"><LogoMark size={26} /></Link>
            <span className="font-cabinet font-black tracking-tight text-lg text-slate-900">
              OPERA<span className="text-sky-600">360</span>
            </span>
            <span className="ml-2 rounded-sm border border-moss/40 bg-moss/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-green-700">
              Portfolio Portal
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
              CRM →
            </Link>
            <span className="hidden sm:block text-xs font-mono text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="technical-card p-6 mb-8 flex flex-col md:flex-row md:items-center gap-5" data-testid="portal-connection-card">
          <div className="flex items-center gap-3 flex-1">
            <span className={`h-2.5 w-2.5 rounded-full ${data?.connected ? "bg-moss animate-pulse" : "bg-cadmium"}`} />
            <div>
              <p className="text-sm font-semibold text-slate-900">
                ScheduleMyRent · {data?.connected ? "Connected" : "Not connected"}
              </p>
              <p className="text-xs text-muted-foreground font-mono mt-0.5">
                {data?.synced_at
                  ? `Last sync ${new Date(data.synced_at).toLocaleString()}${data?.synced_by ? ` by ${data.synced_by}` : ""}`
                  : "Never synced"}
              </p>
            </div>
          </div>
          {data?.error && (
            <p data-testid="portal-error" className="text-xs text-amber-700 leading-relaxed md:max-w-md">{data.error}</p>
          )}
          <button
            data-testid="portal-sync-btn"
            onClick={sync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60 shrink-0"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard icon={Home} label="Occupied Units" value={k.occupied_units ?? "—"} sub={k.total_units != null ? `of ${k.total_units} total units` : null} testid="portal-kpi-occupied" />
          <KpiCard icon={AlertTriangle} label="Vacant Units" value={k.vacant_units ?? "—"} sub={k.vacancy_rate != null ? `${k.vacancy_rate}% vacancy rate` : null} accent testid="portal-kpi-vacancy" />
          <KpiCard icon={Wallet} label={`Collections · ${k.current_month || "This Month"}`} value={k.rent_received_current != null ? money(k.rent_received_current) : "—"} sub={k.rent_due_current != null ? (k.rent_due_current > 0 ? `of ${money(k.rent_due_current)} outstanding (${k.collection_rate_current}%)` : "fully collected — $0 outstanding") : null} testid="portal-kpi-collections" />
          <KpiCard icon={Building2} label="Rent Roll Due" value={k.rent_due_current != null ? money(k.rent_due_current) : "—"} sub={k.current_month ? `${k.current_month} billing` : null} testid="portal-kpi-rentroll" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Wrench, label: "Open Maintenance", value: k.maintenance_open },
            { icon: ShieldCheck, label: "Insurance Review", value: k.insurance_review },
            { icon: FilePlus2, label: "New Applications", value: k.new_applications },
            { icon: CalendarClock, label: "Expiring Leases", value: k.expiring_leases },
          ].map((m, i) => (
            <div key={m.label} data-testid={`portal-secondary-${i}`} className="rounded-md border border-slate-200 bg-slate-50 p-4 flex items-center gap-3">
              <m.icon size={15} className="text-muted-foreground shrink-0" />
              <div>
                <p className="font-mono text-xl font-semibold text-slate-900">{m.value ?? "—"}</p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{m.label}</p>
              </div>
            </div>
          ))}
        </div>

        {collections.length > 0 && (
          <div className="technical-card p-6 mb-10" data-testid="portal-collections-table">
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-5">
              Collections by Rental Month
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {collections.map((c) => {
                const pct = c.due ? Math.round((c.received / c.due) * 100) : 100;
                return (
                  <div key={c.month} className="rounded-md border border-slate-200 bg-slate-50 p-5">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{c.month}</p>
                    <p className="font-mono text-2xl font-semibold text-slate-900">{money(c.received)}</p>
                    <p className="text-xs text-muted-foreground mt-1">of {money(c.due)} due</p>
                    <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 95 ? "bg-moss" : pct >= 70 ? "bg-amber" : "bg-cadmium"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <p className="mt-1.5 text-[10px] font-mono text-muted-foreground">{pct}% collected</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <TrainingImport onImported={load} />

        <KpiCharts kpis={k} collections={collections} history={history} training={training} />

        <PassRateTrends />

        <ScoreHistory batches={batches} />

        {data?.screenshot_path && (
          <div className="technical-card p-6" data-testid="portal-snapshot">
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-4">
              Live Dashboard Snapshot — ScheduleMyRent
            </p>
            <img
              src={`${BACKEND}/api/files/${data.screenshot_path}`}
              alt="ScheduleMyRent landlord dashboard at last sync"
              className="w-full rounded-md border border-slate-200"
            />
          </div>
        )}

        {!data?.connected && !data?.screenshot_path && (
          <div className="technical-card p-14 text-center" data-testid="portal-empty">
            <Building2 size={26} className="mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No portfolio data yet.</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">
              Press Sync Now to pull live KPIs from ScheduleMyRent.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
